import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Circle,
  Flex,
  Grid,
  HStack,
  Icon,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { HiEye, HiEyeSlash, HiClock, HiCube, HiWrenchScrewdriver } from 'react-icons/hi2';
import { toasterApi } from '@ui';
import {
  fabricationReleaseRole,
  FabricationType,
} from '@/types/fabricationType';
import {
  useGetReleasePartProgressQuery,
  useGetReleaseSummaryQuery,
  useManualCompletePartsMutation,
  usePatchSummaryConfigMutation,
} from '@/state/api/fabricationApi';
import PartMasterfilePreview from './PartMasterfilePreview';

type Props = {
  projectId: number;
  releaseId: number;
};

type DraftRow = {
  role: FabricationType.SummaryRow['role'];
  inventory_only: boolean;
  fabrication_required: boolean;
  is_active: boolean;
  notes: string;
};

const PAGE_SIZE = 50;

/**
 * Keeps table + side panel visually capped to the same height on large screens.
 * Adjust this if your surrounding page chrome/header height changes.
 */
const DESKTOP_CONTENT_HEIGHT = 'calc(100vh - 240px)';

const buildDraft = (item: FabricationType.SummaryRow): DraftRow => ({
  role: item.role,
  inventory_only: item.inventory_only,
  fabrication_required: item.fabrication_required,
  is_active: item.is_active,
  notes: item.notes ?? '',
});

const getDisplayValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const metadataEntries = (data: Record<string, any> | null | undefined) => {
  if (!data || typeof data !== 'object') return [];
  return Object.entries(data).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );
};

const CONTROL_SX = {
  bg: 'white',
  color: 'gray.800',
  borderColor: 'gray.300',
  _placeholder: { color: 'gray.500' },
  _dark: {
    bg: '#111318',
    borderColor: '#3A3F4B',
    color: 'white',
    _placeholder: { color: 'gray.500' },
  },
};

const CHECKBOX_CONTROL_SX = {
  bg: 'white',
  borderColor: 'gray.400',
  _dark: {
    bg: '#111318',
    borderColor: '#3A3F4B',
  },
  _checked: { bg: 'blue.500', borderColor: 'blue.500', color: 'white' },
};

const PANEL_STYLES = {
  bg: 'white',
  borderColor: 'gray.200',
  _dark: {
    bg: '#0F1218',
    borderColor: '#293241',
  },
};

const CARD_STYLES = {
  bg: 'gray.50',
  borderColor: 'gray.200',
  _dark: {
    bg: '#151A22',
    borderColor: '#293241',
  },
};

const CARD_SOFT_STYLES = {
  bg: 'gray.100',
  borderColor: 'gray.200',
  _dark: {
    bg: '#121720',
    borderColor: '#293241',
  },
};

const MUTED_TEXT_COLOR = {
  color: 'gray.600',
  _dark: { color: '#98A2B3' },
};

const ACCENT = '#6EA8FE';
const ACCENT_SOFT = '#D7E7FF';

const COMPLETED_HOVER_ROW_BG = { base: 'green.100', _dark: 'green.800' };
const HOVER_ROW_BG = { base: 'gray.100', _dark: 'bg.emphasized' };

const useDebouncedValue = <T,>(value: T, delay = 150) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);

  return debounced;
};

type RowProps = {
  item: FabricationType.SummaryRow;
  rowIndex: number;
  isSaving: boolean;
  isCompleting: boolean;
  isBulkSelected: boolean;
  completionQuantity: string;
  onSave: (item: FabricationType.SummaryRow, draft: DraftRow) => Promise<void>;
  onInspect: (item: FabricationType.SummaryRow) => void;
  onToggleBulkSelected: (partId: number) => void;
  onCompletionQuantityChange: (partId: number, value: string) => void;
  onMarkComplete: (item: FabricationType.SummaryRow) => Promise<void>;
  isSelected: boolean;
};

const ProgressBar: React.FC<{
  value: number;
  label: string;
  qtyCompleted?: number;
  qtyPlanned?: number;
}> = ({ value, label, qtyCompleted, qtyPlanned }) => {
  const safe = Math.max(0, Math.min(100, value));

  const tone =
    safe >= 100 ? '#38A169' : safe >= 60 ? '#5B8DEF' : safe >= 25 ? '#D69E2E' : '#E57373';

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={3}
      {...CARD_SOFT_STYLES}
    >
      <Flex justify="space-between" align="start" gap={3} mb={2}>
        <Box minW={0}>
          <Text fontSize="sm" color="gray.800" _dark={{ color: 'white' }} fontWeight="semibold" lineClamp={1}>
            {label}
          </Text>
          {typeof qtyCompleted === 'number' && typeof qtyPlanned === 'number' ? (
            <Text fontSize="xs" {...MUTED_TEXT_COLOR}>
              {qtyCompleted} / {qtyPlanned} completed
            </Text>
          ) : null}
        </Box>

        <Badge
          bg={`${tone}22`}
          color={tone}
          borderWidth="1px"
          borderColor={`${tone}55`}
          borderRadius="full"
          px={2}
          py={1}
          minW="58px"
          justifyContent="center"
        >
          {safe}%
        </Badge>
      </Flex>

      <Box h="10px" bg="gray.200" _dark={{ bg: '#212733' }} borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          w={`${safe}%`}
          bg={tone}
          borderRadius="full"
          transition="width 0.25s ease"
          boxShadow={`0 0 16px ${tone}55`}
        />
      </Box>
    </Box>
  );
};

const ProgressTimeline: React.FC<{
  timeline: FabricationType.PartProgressTimelineItem[];
  loading: boolean;
}> = ({ timeline, loading }) => {
  if (loading) {
    return (
      <Flex py={8} justify="center">
        <Spinner size="sm" />
      </Flex>
    );
  }

  if (!timeline.length) {
    return (
      <Box borderWidth="1px" borderRadius="lg" p={4} {...CARD_SOFT_STYLES}>
        <Text fontSize="sm" {...MUTED_TEXT_COLOR}>
          No progress has been logged for this part yet.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap={4}>
      {timeline.map((entry, index) => {
        const hasInventoryMovement = !!entry.inventory_delta;
        const isLargeQty = entry.quantity >= 10;

        return (
          <Flex key={entry.id} align="stretch" gap={3}>
            <Flex direction="column" align="center" minW="22px">
              <Circle
                size="14px"
                mt="4px"
                bg={hasInventoryMovement ? '#38A169' : ACCENT}
                border="3px solid"
                borderColor={hasInventoryMovement ? '#B7F5CB' : ACCENT_SOFT}
                boxShadow={`0 0 14px ${hasInventoryMovement ? '#38A16966' : '#6EA8FE55'}`}
              />
              {index < timeline.length - 1 ? (
                <Box
                  flex="1"
                  w="2px"
                  bg="linear-gradient(180deg, #36507A 0%, #273140 100%)"
                  mt={1}
                  minH="56px"
                  borderRadius="full"
                />
              ) : null}
            </Flex>

            <Box
              flex="1"
              borderWidth="1px"
              borderRadius="xl"
              p={3}
              boxShadow="0 8px 24px rgba(0,0,0,0.18)"
              bg="white"
              borderColor="gray.200"
              _dark={{
                bg: '#151A22',
                borderColor: '#293241',
              }}
            >
              <Flex justify="space-between" align="start" gap={3} wrap="wrap" mb={2}>
                <Box minW={0}>
                  <Text fontWeight="bold" color="gray.800" _dark={{ color: 'white' }} lineClamp={1}>
                    {entry.step_name || 'Progress update'}
                  </Text>

                  <HStack gap={2} mt={1} wrap="wrap">
                    <Badge bg="gray.100" color="gray.800" _dark={{ bg: '#1E2633', color: '#D6E4FF' }} borderRadius="full">
                      {entry.user || 'Unknown user'}
                    </Badge>

                    {entry.machine_name ? (
                      <Badge bg="purple.50" color="purple.700" _dark={{ bg: '#2A233A', color: '#E1D2FF' }} borderRadius="full">
                        {entry.machine_name}
                      </Badge>
                    ) : null}

                    {entry.work_order_identifier ? (
                      <Badge bg="green.50" color="green.700" _dark={{ bg: '#1E2B24', color: '#C7F5D8' }} borderRadius="full">
                        {entry.work_order_identifier}
                      </Badge>
                    ) : entry.work_order_id ? (
                      <Badge bg="green.50" color="green.700" _dark={{ bg: '#1E2B24', color: '#C7F5D8' }} borderRadius="full">
                        WO {entry.work_order_id}
                      </Badge>
                    ) : null}
                  </HStack>
                </Box>

                <HStack gap={2} align="center" wrap="wrap">
                  <Badge
                    bg={isLargeQty ? 'blue.50' : 'gray.100'}
                    color="blue.700"
                    _dark={{ bg: isLargeQty ? '#1D3557' : '#24314A', color: '#D6E4FF' }}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    Qty {entry.quantity}
                  </Badge>

                  {hasInventoryMovement ? (
                    <Badge
                      bg="green.100"
                      color="green.800"
                      _dark={{ bg: '#1F4D2E', color: 'white' }}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      Inv {entry.inventory_delta > 0 ? `+${entry.inventory_delta}` : entry.inventory_delta}
                    </Badge>
                  ) : null}
                </HStack>
              </Flex>

              <HStack gap={4} wrap="wrap" mb={entry.note ? 2 : 0}>
                <HStack gap={1}>
                  <Icon as={HiClock} color="gray.500" _dark={{ color: '#98A2B3' }} boxSize={3.5} />
                  <Text fontSize="xs" color="gray.500" _dark={{ color: '#98A2B3' }}>
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'Unknown time'}
                  </Text>
                </HStack>

                {typeof entry.step_sequence === 'number' ? (
                  <HStack gap={1}>
                    <Icon as={HiWrenchScrewdriver} color="gray.500" _dark={{ color: '#98A2B3' }} boxSize={3.5} />
                    <Text fontSize="xs" color="gray.500" _dark={{ color: '#98A2B3' }}>
                      Step {entry.step_sequence}
                    </Text>
                  </HStack>
                ) : null}

                {hasInventoryMovement ? (
                  <HStack gap={1}>
                    <Icon as={HiCube} color="#38A169" boxSize={3.5} />
                    <Text fontSize="xs" color="#38A169">
                      Inventory affected
                    </Text>
                  </HStack>
                ) : null}
              </HStack>

              {entry.note ? (
                <Box
                  mt={2}
                  p={3}
                  borderRadius="md"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  _dark={{
                    bg: '#0F141C',
                    borderColor: '#222A37',
                  }}
                >
                  <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.200' }}>
                    {entry.note}
                  </Text>
                </Box>
              ) : null}
            </Box>
          </Flex>
        );
      })}
    </Stack>
  );
};

const ReleaseSummaryRow = memo(
  ({
    item,
    rowIndex,
    isSaving,
    isCompleting,
    isBulkSelected,
    completionQuantity,
    onSave,
    onInspect,
    onToggleBulkSelected,
    onCompletionQuantityChange,
    onMarkComplete,
    isSelected,
  }: RowProps) => {
    const [draft, setDraft] = useState<DraftRow>(() => buildDraft(item));

    useEffect(() => {
      setDraft(buildDraft(item));
    }, [
      item.role,
      item.inventory_only,
      item.fabrication_required,
      item.is_active,
      item.notes,
    ]);

    const isDirty =
      draft.role !== item.role ||
      draft.inventory_only !== item.inventory_only ||
      draft.fabrication_required !== item.fabrication_required ||
      draft.is_active !== item.is_active ||
      draft.notes !== (item.notes ?? '');

    const metadata = item.data ?? {};
    const description = metadata.description;
    const finish = metadata.finish;
    const length = metadata.length;
    const mark = metadata.item;
    const dieAlloy = metadata.die_alloy;
    const frameNames = item.frame_names ?? [];
    const isFullyCompleted = Number(item.remaining_qty ?? 0) === 0;

    const lightDefaultRowBg = rowIndex % 2 === 0 ? 'white' : 'gray.50';
    const darkDefaultRowBg = rowIndex % 2 === 0 ? '#1A1D24' : '#15181E';

    const baseCellProps = {
      bg: lightDefaultRowBg,
      color: 'gray.800',
      _dark: {
        bg: darkDefaultRowBg,
        color: 'white',
      },
      transition: 'background 0.15s ease, color 0.15s ease',
    };

    const selectedCellProps = isFullyCompleted
      ? {
          bg: isSelected ? 'green.100' : 'green.50',
          color: 'gray.900',
          _dark: {
            bg: isSelected ? '#25663B' : '#1F4D2E',
            color: 'white',
          },
        }
      : isSelected
      ? {
          bg: 'blue.50',
          color: 'gray.900',
          _dark: {
            bg: '#20232B',
            color: 'white',
          },
        }
      : baseCellProps;

    const hoverCellProps = {
      bg: isFullyCompleted ? COMPLETED_HOVER_ROW_BG : HOVER_ROW_BG,
      color: { base: 'gray.900', _dark: 'white' },
    };

    return (
      <Table.Row
        _hover={{
          '& td': hoverCellProps,
        }}
      >
        <Table.Cell textAlign="center" data-sticky="start" left="0" zIndex={1} {...selectedCellProps}>
          <Checkbox.Root
            checked={isBulkSelected}
            disabled={item.remaining_qty <= 0}
            onCheckedChange={() => onToggleBulkSelected(item.part_id)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
              <Checkbox.Indicator />
            </Checkbox.Control>
          </Checkbox.Root>
        </Table.Cell>

        <Table.Cell minW="260px" data-sticky="start" left="52px" zIndex={1} {...selectedCellProps}>
          <Stack gap={1}>
            <Text fontWeight="medium" color="gray.800" _dark={{ color: 'white' }}>
              {item.part_number}
            </Text>
            <HStack gap={2} wrap="wrap">
              <Badge bg="gray.100" color="gray.800" _dark={{ bg: '#2A2F39', color: 'white' }}>
                {item.inventory_only ? 'Inventory only' : 'General'}
              </Badge>

              {frameNames.length ? (
                <Badge variant="subtle" bg="gray.100" color="gray.800" _dark={{ bg: '#2A2F39', color: 'white' }}>
                  {item.frame_count === 1 ? `Frame ${frameNames[0]}` : `${item.frame_count} frames`}
                </Badge>
              ) : (
                <Badge variant="subtle" bg="gray.100" color="gray.800" _dark={{ bg: '#2A2F39', color: 'white' }}>
                  No frame
                </Badge>
              )}

              {mark ? (
                <Badge variant="subtle" bg="gray.100" color="gray.800" _dark={{ bg: '#2A2F39', color: 'white' }}>
                  Item {mark}
                </Badge>
              ) : null}

              {isFullyCompleted ? (
                <Badge bg="green.500" color="white">
                  Complete
                </Badge>
              ) : null}
            </HStack>
          </Stack>
        </Table.Cell>

        <Table.Cell minW="360px" {...selectedCellProps}>
          <Stack gap={1}>
            <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }} lineClamp={1}>
              <Text as="span" fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                Desc:
              </Text>{' '}
              {getDisplayValue(description)}
            </Text>

            <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }} lineClamp={1}>
              <Text as="span" fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                Finish:
              </Text>{' '}
              {getDisplayValue(finish)}
            </Text>

            <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }}>
              <Text as="span" fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                Length:
              </Text>{' '}
              {getDisplayValue(length)} ·{' '}
              <Text as="span" fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                Alloy:
              </Text>{' '}
              {getDisplayValue(dieAlloy)}
            </Text>

            <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }} lineClamp={2}>
              <Text as="span" fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                Frames:
              </Text>{' '}
              {frameNames.length ? frameNames.join(', ') : '—'}
            </Text>

            <Button
              size="xs"
              variant="ghost"
              width="fit-content"
              px={2}
              minW="unset"
              h="30px"
              color={isSelected ? 'blue.700' : 'gray.700'}
              bg={isSelected ? 'blue.50' : 'transparent'}
              borderWidth="1px"
              borderColor={isSelected ? 'blue.200' : 'transparent'}
              _dark={{
                color: isSelected ? ACCENT_SOFT : 'gray.300',
                bg: isSelected ? '#1C2A3F' : 'transparent',
                borderColor: isSelected ? '#35507A' : 'transparent',
              }}
              _hover={{ bg: 'gray.100', color: 'gray.900', borderColor: 'gray.300' }}
              onClick={() => onInspect(item)}
            >
              <HStack gap={1}>
                <Icon as={HiEye} boxSize={4} />
                <Text fontSize="xs" fontWeight="medium">
                  View
                </Text>
              </HStack>
            </Button>
          </Stack>
        </Table.Cell>

        <Table.Cell textAlign="end" {...selectedCellProps}>
          <Text color="inherit">{item.required_qty}</Text>
        </Table.Cell>
        <Table.Cell textAlign="end" {...selectedCellProps}>
          <Text color="inherit">{item.fabricated_qty}</Text>
        </Table.Cell>
        <Table.Cell textAlign="end" {...selectedCellProps}>
          <Text color="inherit">{item.open_work_order_qty}</Text>
        </Table.Cell>
        <Table.Cell textAlign="end" {...selectedCellProps}>
          <Text color="inherit">{item.remaining_qty}</Text>
        </Table.Cell>

        <Table.Cell minW="180px" {...selectedCellProps}>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={draft.role}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  role: e.target.value as DraftRow['role'],
                }))
              }
              {...CONTROL_SX}
            >
              {Object.values(fabricationReleaseRole).map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Table.Cell>

        <Table.Cell textAlign="center" {...selectedCellProps}>
          <Checkbox.Root
            checked={draft.inventory_only}
            onCheckedChange={(details) =>
              setDraft((prev) => ({
                ...prev,
                inventory_only: details.checked === true,
              }))
            }
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
              <Checkbox.Indicator />
            </Checkbox.Control>
          </Checkbox.Root>
        </Table.Cell>

        <Table.Cell textAlign="center" {...selectedCellProps}>
          <Checkbox.Root
            checked={draft.fabrication_required}
            onCheckedChange={(details) =>
              setDraft((prev) => ({
                ...prev,
                fabrication_required: details.checked === true,
              }))
            }
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
              <Checkbox.Indicator />
            </Checkbox.Control>
          </Checkbox.Root>
        </Table.Cell>

        <Table.Cell textAlign="center" {...selectedCellProps}>
          <Checkbox.Root
            checked={draft.is_active}
            onCheckedChange={(details) =>
              setDraft((prev) => ({
                ...prev,
                is_active: details.checked === true,
              }))
            }
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
              <Checkbox.Indicator />
            </Checkbox.Control>
          </Checkbox.Root>
        </Table.Cell>

        <Table.Cell minW="240px" {...selectedCellProps}>
          <Input
            size="sm"
            value={draft.notes}
            onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
            {...CONTROL_SX}
          />
        </Table.Cell>

        <Table.Cell {...selectedCellProps}>
          <HStack>
            <Button size="sm" onClick={() => onSave(item, draft)} loading={isSaving} disabled={!isDirty}>
              Save
            </Button>
            {isDirty ? (
              <Button size="sm" variant="ghost" onClick={() => setDraft(buildDraft(item))}>
                Reset
              </Button>
            ) : null}
          </HStack>
        </Table.Cell>

        <Table.Cell minW="220px" {...selectedCellProps}>
          <HStack>
            <Input
              size="sm"
              type="number"
              min={1}
              max={item.remaining_qty}
              value={completionQuantity}
              disabled={item.remaining_qty <= 0}
              onChange={(e) => onCompletionQuantityChange(item.part_id, e.target.value)}
              {...CONTROL_SX}
            />
            <Button
              size="sm"
              colorPalette="green"
              loading={isCompleting}
              disabled={item.remaining_qty <= 0}
              onClick={() => onMarkComplete(item)}
            >
              Mark fabricated
            </Button>
          </HStack>
        </Table.Cell>
      </Table.Row>
    );
  },
);

ReleaseSummaryRow.displayName = 'ReleaseSummaryRow';

const ReleaseItemsTable: React.FC<Props> = ({ projectId, releaseId }) => {
  const { data, isLoading, isFetching, refetch: refetchSummary } = useGetReleaseSummaryQuery({
    projectId,
    releaseId,
  });

  const [patchSummaryConfig] = usePatchSummaryConfigMutation();
  const [manualCompleteParts, { isLoading: isCompletingParts }] =
    useManualCompletePartsMutation();
  const [savingPartId, setSavingPartId] = useState<number | null>(null);
  const [completingPartId, setCompletingPartId] = useState<number | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 150);

  const [roleFilter, setRoleFilter] = useState<string>('');
  const [fabricateOnly, setFabricateOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [inventoryOnly, setInventoryOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
  const [isInspectorOpen, setIsInspectorOpen] = useState(true);
  const [selectedCompletionPartIds, setSelectedCompletionPartIds] = useState<number[]>([]);
  const [completionQuantities, setCompletionQuantities] = useState<Record<number, string>>({});
  const [completionNotes, setCompletionNotes] = useState('');

  const rows = useMemo(() => data ?? [], [data]);

  const filteredRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();

    return rows.filter((item) => {
      if (q) {
        const haystack = [
          item.part_number,
          item.data?.description,
          item.data?.finish,
          item.data?.item,
          item.data?.die_alloy,
          item.notes,
          ...(item.frame_names ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(q)) return false;
      }

      if (roleFilter && item.role !== roleFilter) return false;
      if (fabricateOnly && !item.fabrication_required) return false;
      if (activeOnly && !item.is_active) return false;
      if (inventoryOnly && !item.inventory_only) return false;

      return true;
    });
  }, [rows, debouncedSearch, roleFilter, fabricateOnly, activeOnly, inventoryOnly]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, safePage]);

  const selectedItem = useMemo(() => {
    if (!selectedPartId) return null;
    return rows.find((item) => item.part_id === selectedPartId) ?? null;
  }, [rows, selectedPartId]);

  const {
    data: selectedProgress,
    isFetching: isProgressFetching,
    refetch: refetchProgress,
  } = useGetReleasePartProgressQuery(
    {
      projectId,
      releaseId,
      partId: selectedPartId as number,
    },
    {
      skip: !selectedPartId,
    },
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleFilter, fabricateOnly, activeOnly, inventoryOnly]);

  const handleSave = useCallback(
    async (item: FabricationType.SummaryRow, draft: DraftRow) => {
      try {
        setSavingPartId(item.part_id);

        await patchSummaryConfig({
          projectId,
          releaseId,
          partId: item.part_id,
          body: {
            role: draft.role,
            inventory_only: draft.inventory_only,
            fabrication_required: draft.fabrication_required,
            is_active: draft.is_active,
            notes: draft.notes,
          },
        }).unwrap();

        toasterApi({
          type: 'success',
          message: `Updated ${item.part_number}`,
        });
      } catch (error: any) {
        toasterApi({
          type: 'error',
          message: 'Save failed.',
          detail: error?.data?.detail ?? 'Could not update fabrication config.',
        });
      } finally {
        setSavingPartId(null);
      }
    },
    [patchSummaryConfig, projectId, releaseId],
  );

  const handleInspect = useCallback((item: FabricationType.SummaryRow) => {
    setSelectedPartId(item.part_id);
    setIsInspectorOpen(true);
  }, []);

  const getCompletionQuantity = useCallback(
    (item: FabricationType.SummaryRow) =>
      completionQuantities[item.part_id] ?? String(Math.max(item.remaining_qty, 0)),
    [completionQuantities],
  );

  const handleCompletionQuantityChange = useCallback((partId: number, value: string) => {
    if (value !== '' && !/^\d+$/.test(value)) return;
    setCompletionQuantities((prev) => ({
      ...prev,
      [partId]: value,
    }));
  }, []);

  const toggleCompletionSelection = useCallback((partId: number) => {
    setSelectedCompletionPartIds((prev) =>
      prev.includes(partId)
        ? prev.filter((id) => id !== partId)
        : [...prev, partId],
    );
  }, []);

  const refreshAfterCompletion = useCallback(async () => {
    await refetchSummary();
    if (selectedPartId) {
      await refetchProgress();
    }
  }, [refetchSummary, refetchProgress, selectedPartId]);

  const markSingleComplete = useCallback(
    async (item: FabricationType.SummaryRow) => {
      const quantity = Number(getCompletionQuantity(item));
      if (!Number.isFinite(quantity) || quantity <= 0 || quantity > item.remaining_qty) {
        toasterApi({
          type: 'warning',
          message: 'Invalid completion quantity.',
          detail: `Enter a quantity between 1 and ${item.remaining_qty}.`,
        });
        return;
      }

      try {
        setCompletingPartId(item.part_id);
        await manualCompleteParts({
          projectId,
          releaseId,
          body: {
            items: [
              {
                part_id: item.part_id,
                quantity,
                notes: completionNotes || null,
              },
            ],
          },
        }).unwrap();

        toasterApi({
          type: 'success',
          message: `Marked ${item.part_number} fabricated.`,
          detail: `Completed quantity ${quantity}.`,
        });
        setCompletionQuantities((prev) => {
          const next = { ...prev };
          delete next[item.part_id];
          return next;
        });
        await refreshAfterCompletion();
      } catch (error: any) {
        toasterApi({
          type: 'error',
          message: 'Completion failed.',
          detail: error?.data?.detail ?? 'Could not mark this part fabricated.',
        });
      } finally {
        setCompletingPartId(null);
      }
    },
    [
      completionNotes,
      getCompletionQuantity,
      manualCompleteParts,
      projectId,
      releaseId,
      refreshAfterCompletion,
    ],
  );

  const markBulkComplete = useCallback(async () => {
    const selectedRows = rows.filter((item) => selectedCompletionPartIds.includes(item.part_id));
    const items = selectedRows
      .map((item) => ({
        item,
        quantity: Number(getCompletionQuantity(item)),
      }))
      .filter(({ item, quantity }) => item.remaining_qty > 0 && quantity > 0);

    const invalid = items.find(
      ({ item, quantity }) => !Number.isFinite(quantity) || quantity > item.remaining_qty,
    );
    if (invalid) {
      toasterApi({
        type: 'warning',
        message: 'Invalid bulk quantity.',
        detail: `${invalid.item.part_number} must be between 1 and ${invalid.item.remaining_qty}.`,
      });
      return;
    }

    if (!items.length) {
      toasterApi({
        type: 'warning',
        message: 'Select at least one part with remaining quantity.',
      });
      return;
    }

    if (
      items.length >= 5 &&
      !window.confirm(`Mark ${items.length} parts as fabricated? This will record manual fabrication completions against the production summary.`)
    ) {
      return;
    }

    try {
      await manualCompleteParts({
        projectId,
        releaseId,
        body: {
          items: items.map(({ item, quantity }) => ({
            part_id: item.part_id,
            quantity,
            notes: completionNotes || null,
          })),
        },
      }).unwrap();

      toasterApi({
        type: 'success',
        message: 'Bulk fabrication completion saved.',
        detail: `${items.length} part(s) updated.`,
      });
      setSelectedCompletionPartIds([]);
      setCompletionQuantities((prev) => {
        const next = { ...prev };
        items.forEach(({ item }) => {
          delete next[item.part_id];
        });
        return next;
      });
      await refreshAfterCompletion();
    } catch (error: any) {
      toasterApi({
        type: 'error',
        message: 'Bulk completion failed.',
        detail: error?.data?.detail ?? 'Could not mark selected parts fabricated.',
      });
    }
  }, [
    completionNotes,
    getCompletionQuantity,
    manualCompleteParts,
    projectId,
    releaseId,
    refreshAfterCompletion,
    rows,
    selectedCompletionPartIds,
  ]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setRoleFilter('');
    setFabricateOnly(false);
    setActiveOnly(false);
    setInventoryOnly(false);
  }, []);

  const summaryStats = useMemo(() => {
    return filteredRows.reduce(
      (acc, item) => {
        acc.required += item.required_qty;
        acc.fabricated += item.fabricated_qty;
        acc.open += item.open_work_order_qty;
        acc.remaining += item.remaining_qty;
        return acc;
      },
      { required: 0, fabricated: 0, open: 0, remaining: 0 },
    );
  }, [filteredRows]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!rows.length) {
    return (
      <Box borderWidth="1px" borderRadius="md" p={6}>
        <Text>No fabrication summary rows exist for this release.</Text>
      </Box>
    );
  }

  const inspectorVisible = Boolean(selectedItem && isInspectorOpen);

  return (
    <Stack
      borderWidth="1px"
      borderRadius="md"
      p={4}
      gap={4}
      minH={0}
      bg="white"
      borderColor="gray.200"
      _dark={{
        bg: 'transparent',
        borderColor: '#293241',
      }}
    >
      <Flex justify="space-between" align="start" wrap="wrap" gap={3}>
        <Box>
          <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
            Fabrication production summary
          </Text>
          <Text color="gray.600" _dark={{ color: 'gray.400' }} fontSize="sm">
            Aggregated by part for this release.
          </Text>
          {isFetching ? <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>Refreshing…</Text> : null}
        </Box>

        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsInspectorOpen((prev) => !prev)}
          disabled={!selectedItem}
          px={3}
          h="36px"
          borderWidth="1px"
          borderColor={inspectorVisible ? 'blue.200' : 'gray.300'}
          bg={inspectorVisible ? 'blue.50' : 'white'}
          color={inspectorVisible ? 'blue.700' : 'gray.700'}
          _dark={{
            borderColor: inspectorVisible ? '#35507A' : '#2C3442',
            bg: inspectorVisible ? '#182334' : '#10151D',
            color: inspectorVisible ? ACCENT_SOFT : 'gray.300',
          }}
          _hover={{
            bg: 'gray.100',
            color: 'gray.900',
            borderColor: 'gray.400',
          }}
        >
          <HStack gap={2}>
            <Icon as={inspectorVisible ? HiEyeSlash : HiEye} boxSize={4} />
            <Text>{inspectorVisible ? 'Hide panel' : 'Show panel'}</Text>
          </HStack>
        </Button>
      </Flex>

      <Flex gap={3} wrap="wrap">
        <Box borderWidth="1px" borderRadius="md" p={3} minW="180px" flex="1" bg="white" borderColor="gray.200" _dark={{ bg: '#151A22', borderColor: '#293241' }}>
          <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
            Total Required
          </Text>
          <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }}>{summaryStats.required}</Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" p={3} minW="180px" flex="1" bg="white" borderColor="gray.200" _dark={{ bg: '#151A22', borderColor: '#293241' }}>
          <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
            Fabricated
          </Text>
          <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }}>{summaryStats.fabricated}</Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" p={3} minW="180px" flex="1" bg="white" borderColor="gray.200" _dark={{ bg: '#151A22', borderColor: '#293241' }}>
          <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
            Open WO
          </Text>
          <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }}>{summaryStats.open}</Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" p={3} minW="180px" flex="1" bg="white" borderColor="gray.200" _dark={{ bg: '#151A22', borderColor: '#293241' }}>
          <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
            Remaining
          </Text>
          <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }}>{summaryStats.remaining}</Text>
        </Box>
      </Flex>

      <Box borderWidth="1px" borderRadius="md" p={4} bg="white" borderColor="gray.200" _dark={{ bg: '#151A22', borderColor: '#293241' }}>
        <Stack gap={3}>
          <Flex gap={3} wrap="wrap">
            <Input
              maxW="360px"
              placeholder="Search part, description, frames, finish, alloy..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              {...CONTROL_SX}
            />

            <NativeSelect.Root maxW="220px">
              <NativeSelect.Field
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                {...CONTROL_SX}
              >
                <option value="">All roles</option>
                {Object.values(fabricationReleaseRole).map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <Button variant="outline" onClick={clearFilters}>
              Reset filters
            </Button>
          </Flex>

          <Flex gap={6} wrap="wrap">
            <Checkbox.Root checked={fabricateOnly} onCheckedChange={(details) => setFabricateOnly(details.checked === true)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label color="gray.800" _dark={{ color: 'white' }}>Fabricate only</Checkbox.Label>
            </Checkbox.Root>

            <Checkbox.Root checked={activeOnly} onCheckedChange={(details) => setActiveOnly(details.checked === true)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label color="gray.800" _dark={{ color: 'white' }}>Active only</Checkbox.Label>
            </Checkbox.Root>

            <Checkbox.Root checked={inventoryOnly} onCheckedChange={(details) => setInventoryOnly(details.checked === true)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control {...CHECKBOX_CONTROL_SX}>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label color="gray.800" _dark={{ color: 'white' }}>Inventory only</Checkbox.Label>
            </Checkbox.Root>
          </Flex>
        </Stack>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={4} bg="white" borderColor="gray.200" _dark={{ bg: '#151A22', borderColor: '#293241' }}>
        <Flex gap={3} wrap="wrap" align="center" justify="space-between">
          <Stack gap={1} minW="240px">
            <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
              Manual fabrication completion
            </Text>
            <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
              Select parts, enter quantities, and sync completed fabrication quantities.
            </Text>
          </Stack>

          <Input
            maxW="360px"
            placeholder="Completion note (optional)"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            {...CONTROL_SX}
          />

          <Button
            colorPalette="green"
            loading={isCompletingParts && !completingPartId}
            disabled={!selectedCompletionPartIds.length}
            onClick={markBulkComplete}
          >
            Bulk mark fabricated ({selectedCompletionPartIds.length})
          </Button>
        </Flex>
      </Box>

      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
          Showing {(safePage - 1) * PAGE_SIZE + (pagedRows.length ? 1 : 0)}–{(safePage - 1) * PAGE_SIZE + pagedRows.length} of {filteredRows.length}
        </Text>

        <HStack>
          <Button size="sm" variant="outline" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={safePage <= 1}>
            Previous
          </Button>
          <Text fontSize="sm" color="gray.800" _dark={{ color: 'white' }}>
            Page {safePage} / {totalPages}
          </Text>
          <Button size="sm" variant="outline" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={safePage >= totalPages}>
            Next
          </Button>
        </HStack>
      </Flex>

      <Grid
        templateColumns={
          inspectorVisible
            ? { base: '1fr', '2xl': 'minmax(0,1fr) 460px' }
            : { base: '1fr' }
        }
        gap={4}
        alignItems="stretch"
        minH={0}
      >
        <Box
          minW={0}
          minH={0}
          h={{ base: 'auto', '2xl': inspectorVisible ? DESKTOP_CONTENT_HEIGHT : 'auto' }}
          display="flex"
          flexDirection="column"
        >
          <Table.ScrollArea
            width="full"
            borderWidth="1px"
            borderRadius="md"
            flex="1"
            minH={0}
            h={{ base: 'auto', '2xl': inspectorVisible ? '100%' : 'auto' }}
            maxH={{ base: 'unset', '2xl': inspectorVisible ? '100%' : 'unset' }}
            overflow="auto"
            bg="white"
            borderColor="gray.200"
            _dark={{
              bg: '#0F1218',
              borderColor: '#293241',
            }}
          >
            <Table.Root
              size="sm"
              variant="outline"
              interactive
              stickyHeader
              showColumnBorder
              color="gray.800"
              _dark={{ color: 'white' }}
              css={{
                '& thead th': {
                  color: 'var(--chakra-colors-gray-900)',
                  background: 'var(--chakra-colors-gray-100)',
                  borderColor: 'var(--chakra-colors-gray-200)',
                },
                '& tbody td': {
                  color: 'var(--chakra-colors-gray-800)',
                  borderColor: 'var(--chakra-colors-gray-200)',
                },
                '& tbody tr:hover td': {
                  color: 'var(--chakra-colors-gray-900)',
                },

                '.dark & thead th': {
                  color: '#FFFFFF',
                  background: '#20232B',
                  borderColor: '#323846',
                },
                '.dark & tbody td': {
                  color: '#FFFFFF',
                  borderColor: '#323846',
                },
                '.dark & tbody tr:hover td': {
                  color: '#FFFFFF',
                },

                '& td, & th': {
                  verticalAlign: 'middle',
                },
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader
                    color="gray.900"
                    _dark={{ color: 'white' }}
                    textAlign="center"
                    data-sticky="start"
                    left="0"
                    zIndex={2}
                    minW="52px"
                  >
                    Select
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    minW="260px"
                    data-sticky="start"
                    left="52px"
                    zIndex={2}
                    bg="gray.100"
                    color="gray.900"
                    _dark={{ bg: '#20232B', color: 'white' }}
                  >
                    Part
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} minW="360px">
                    Metadata
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="end">
                    Required
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="end">
                    Fabricated
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="end">
                    Open WO
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="end">
                    Remaining
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} minW="180px">
                    Role
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="center">
                    Inv only
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="center">
                    Fabricate
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} textAlign="center">
                    Active
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} minW="240px">
                    Notes
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} minW="140px" />
                  <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }} minW="220px">
                    Complete
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {pagedRows.map((item, index) => (
                  <ReleaseSummaryRow
                    key={item.part_id}
                    item={item}
                    rowIndex={index}
                    isSaving={savingPartId === item.part_id}
                    isCompleting={completingPartId === item.part_id}
                    isBulkSelected={selectedCompletionPartIds.includes(item.part_id)}
                    completionQuantity={getCompletionQuantity(item)}
                    onSave={handleSave}
                    onInspect={handleInspect}
                    onToggleBulkSelected={toggleCompletionSelection}
                    onCompletionQuantityChange={handleCompletionQuantityChange}
                    onMarkComplete={markSingleComplete}
                    isSelected={selectedPartId === item.part_id}
                  />
                ))}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        </Box>

        {inspectorVisible && selectedItem ? (
          <Box
            borderWidth="1px"
            borderRadius="xl"
            overflow="hidden"
            minH={0}
            h={{ base: 'auto', '2xl': DESKTOP_CONTENT_HEIGHT }}
            boxShadow="0 16px 40px rgba(0,0,0,0.28)"
            display="flex"
            flexDirection="column"
            {...PANEL_STYLES}
          >
            <Flex
              px={4}
              py={3}
              justify="space-between"
              align="start"
              borderBottomWidth="1px"
              bg="linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)"
              _dark={{
                bg: 'linear-gradient(180deg, #151C26 0%, #111722 100%)',
              }}
              borderColor="gray.200"
              flexShrink={0}
            >
              <Box>
                <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }}>
                  {selectedItem.part_number}
                </Text>
                <Text fontSize="sm" color="gray.600" _dark={{ color: '#98A2B3' }}>
                  Part details, PDF, and fabrication progress
                </Text>
              </Box>

              <HStack>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setIsInspectorOpen(false)}
                  color="gray.700"
                  borderWidth="1px"
                  borderColor="gray.300"
                  _dark={{
                    color: 'gray.300',
                    borderColor: '#2E3848',
                  }}
                  _hover={{ bg: 'gray.100', color: 'gray.900' }}
                >
                  <HStack gap={1}>
                    <Icon as={HiEyeSlash} boxSize={3.5} />
                    <Text>Hide</Text>
                  </HStack>
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setSelectedPartId(null)}>
                  Clear
                </Button>
              </HStack>
            </Flex>

            <Stack
              gap={4}
              p={4}
              flex="1"
              minH={0}
              overflowY={{ base: 'visible', '2xl': 'auto' }}
            >
              <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                  <Box p={3} borderRadius="lg" bg="white" _dark={{ bg: '#10161F' }}>
                    <Text fontSize="xs" color="gray.600" _dark={{ color: '#98A2B3' }}>Required</Text>
                    <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }} fontSize="lg">{selectedItem.required_qty}</Text>
                  </Box>
                  <Box p={3} borderRadius="lg" bg="white" _dark={{ bg: '#10161F' }}>
                    <Text fontSize="xs" color="gray.600" _dark={{ color: '#98A2B3' }}>Fabricated</Text>
                    <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }} fontSize="lg">{selectedItem.fabricated_qty}</Text>
                  </Box>
                  <Box p={3} borderRadius="lg" bg="white" _dark={{ bg: '#10161F' }}>
                    <Text fontSize="xs" color="gray.600" _dark={{ color: '#98A2B3' }}>Open WO</Text>
                    <Text fontWeight="bold" color="gray.900" _dark={{ color: 'white' }} fontSize="lg">{selectedItem.open_work_order_qty}</Text>
                  </Box>
                  <Box p={3} borderRadius="lg" bg="white" _dark={{ bg: '#10161F' }}>
                    <Text fontSize="xs" color="gray.600" _dark={{ color: '#98A2B3' }}>Remaining</Text>
                    <Text
                      fontWeight="bold"
                      color={selectedItem.remaining_qty === 0 ? '#38A169' : 'gray.900'}
                      _dark={{ color: selectedItem.remaining_qty === 0 ? '#7FD99A' : 'white' }}
                      fontSize="lg"
                    >
                      {selectedItem.remaining_qty}
                    </Text>
                  </Box>
                </Grid>
              </Box>

              <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }} mb={3}>
                  Step progress
                </Text>

                <Stack gap={3}>
                  {(selectedProgress?.progress_steps?.length
                    ? selectedProgress.progress_steps
                    : selectedItem.progress_steps_preview || []
                  ).map((step) => (
                    <ProgressBar
                      key={`${step.sequence}-${step.step_name}`}
                      label={`${step.sequence}. ${step.step_name}`}
                      value={step.completion_pct}
                      qtyCompleted={step.qty_completed}
                      qtyPlanned={step.qty_planned}
                    />
                  ))}
                </Stack>
              </Box>

              {selectedProgress?.work_orders?.length ? (
                <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                      Work orders
                    </Text>
                    <Badge bg="blue.50" color="blue.700" _dark={{ bg: '#1C2A3F', color: ACCENT_SOFT }} borderRadius="full">
                      {selectedProgress.work_orders.length}
                    </Badge>
                  </Flex>

                  <Stack gap={2}>
                    {selectedProgress.work_orders.map((wo) => (
                      <Box
                        key={wo.id}
                        borderWidth="1px"
                        borderRadius="lg"
                        p={3}
                        bg="white"
                        borderColor="gray.200"
                        _dark={{ bg: '#10161F', borderColor: '#293241' }}
                      >
                        <Flex justify="space-between" align="start" gap={3} wrap="wrap">
                          <Box minW={0}>
                            <Text fontWeight="medium" color="gray.900" _dark={{ color: 'white' }} lineClamp={1}>
                              {wo.identifier || `WO-${wo.id}`}
                            </Text>
                            {wo.process_name ? (
                              <Text fontSize="xs" {...MUTED_TEXT_COLOR}>
                                {wo.process_name}
                              </Text>
                            ) : null}
                          </Box>

                          <HStack gap={2} wrap="wrap" flexShrink={0}>
                            <Badge
                              bg={
                                wo.status === 'COMPLETED' ? 'green.50' :
                                wo.status === 'IN_PROGRESS' ? 'orange.50' :
                                wo.status === 'ON_HOLD' ? 'purple.50' :
                                wo.status === 'CANCELLED' ? 'red.50' :
                                'gray.100'
                              }
                              color={
                                wo.status === 'COMPLETED' ? 'green.700' :
                                wo.status === 'IN_PROGRESS' ? 'orange.700' :
                                wo.status === 'ON_HOLD' ? 'purple.700' :
                                wo.status === 'CANCELLED' ? 'red.700' :
                                'gray.700'
                              }
                              _dark={{
                                bg: wo.status === 'COMPLETED' ? '#1E2B24' :
                                    wo.status === 'IN_PROGRESS' ? '#2B1E10' :
                                    wo.status === 'ON_HOLD' ? '#231E2B' :
                                    wo.status === 'CANCELLED' ? '#2B1E1E' :
                                    '#1C2433',
                                color: wo.status === 'COMPLETED' ? '#C7F5D8' :
                                       wo.status === 'IN_PROGRESS' ? '#FDD9B0' :
                                       wo.status === 'ON_HOLD' ? '#E1D2FF' :
                                       wo.status === 'CANCELLED' ? '#FDD0D0' :
                                       '#D6E4FF',
                              }}
                              borderRadius="full"
                              fontSize="xs"
                            >
                              {wo.status}
                            </Badge>
                            <Text fontSize="xs" {...MUTED_TEXT_COLOR}>
                              {wo.qty_completed}/{wo.qty_planned}
                            </Text>
                          </HStack>
                        </Flex>

                        {wo.due_date ? (
                          <HStack gap={1} mt={2}>
                            <Icon as={HiClock} color={MUTED_TEXT_COLOR.color} _dark={{ color: MUTED_TEXT_COLOR._dark.color }} boxSize={3} />
                            <Text fontSize="xs" {...MUTED_TEXT_COLOR}>
                              Due {new Date(wo.due_date).toLocaleDateString()}
                            </Text>
                          </HStack>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }}>
                    Progress timeline
                  </Text>
                  <Badge bg="blue.50" color="blue.700" _dark={{ bg: '#1C2A3F', color: ACCENT_SOFT }} borderRadius="full">
                    {selectedProgress?.timeline?.length ?? 0} events
                  </Badge>
                </Flex>

                <ProgressTimeline
                  timeline={selectedProgress?.timeline ?? []}
                  loading={isProgressFetching}
                />
              </Box>

              <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }} mb={3}>
                  Frames
                </Text>
                <Text color="gray.700" _dark={{ color: 'gray.300' }}>
                  {selectedItem.frame_names?.length
                    ? selectedItem.frame_names.join(', ')
                    : 'No frames'}
                </Text>
              </Box>

              <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }} mb={3}>
                  Metadata
                </Text>

                <Table.ScrollArea width="full">
                  <Table.Root size="sm" variant="line">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader minW="180px" color="gray.900" _dark={{ color: 'white' }}>Key</Table.ColumnHeader>
                        <Table.ColumnHeader color="gray.900" _dark={{ color: 'white' }}>Value</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {metadataEntries(selectedItem.data).map(([key, value]) => (
                        <Table.Row key={key}>
                          <Table.Cell fontWeight="medium" color="gray.900" _dark={{ color: 'white' }}>
                            {key}
                          </Table.Cell>
                          <Table.Cell color="gray.700" _dark={{ color: 'gray.300' }}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
              </Box>

              <Box borderWidth="1px" borderRadius="xl" p={3} {...CARD_STYLES}>
                <Text fontWeight="semibold" color="gray.900" _dark={{ color: 'white' }} mb={3}>
                  Part drawing
                </Text>

                <PartMasterfilePreview
                  projectId={projectId}
                  partNumber={selectedItem.part_number}
                  title="Part PDF preview"
                  minH="560px"
                  emptyText="Upload the part PDF using the part number as the filename to preview it here."
                />
              </Box>
            </Stack>
          </Box>
        ) : null}
      </Grid>
    </Stack>
  );
};

export default ReleaseItemsTable;